import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { AddFriends } from '@sorare/core/src/atoms/icons/AddFriends';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { Card } from '@sorare/core/src/components/card/Card';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';

import type { SlotsPreview_cardCollection } from './__generated__/index.graphql';

const Root = styled.div`
  width: 100%;
  overflow: hidden;
  padding: 0 var(--double-unit);
`;
const EmptySlotWrapper = styled(Horizontal).attrs({ center: true })`
  height: 100%;
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-50-opaque);
  backdrop-filter: blur(8px);
  aspect-ratio: var(--card-aspect-ratio);
`;

type Props = {
  cardCollection: SlotsPreview_cardCollection;
  shouldBeHighlighted: Nullable<boolean>;
};
export const SlotsPreview = ({
  cardCollection,
  shouldBeHighlighted,
}: Props) => {
  const { slotsPreview, snapshotted } = cardCollection;

  return (
    <Root>
      <Scrollable
        itemToDisplay={3}
        overhang={3}
        scrollButtonsProps={{ hideButtonsOnMobile: true }}
        contained
        itemGap={1}
      >
        {slotsPreview.map(
          (
            {
              slug,
              highlightedCardCollectionCard,
              slot,
              cardCollectionCardsCount,
            },
            i
          ) => {
            if (snapshotted && cardCollectionCardsCount > 0) {
              return (
                <CardImg key={slug} width={320} src={slot.cardPictureUrl} />
              );
            }
            if (highlightedCardCollectionCard) {
              return (
                <OnboardingHighlightWrapper
                  key={slug}
                  highlightName="CollectionPreviewCard"
                  wrap={i === 0 && !!shouldBeHighlighted}
                >
                  <Card card={highlightedCardCollectionCard.anyCard} />
                </OnboardingHighlightWrapper>
              );
            }
            return (
              <EmptySlotWrapper key={slug}>
                {!snapshotted && (
                  <Vertical gap={0.5} center>
                    <LabelS color="var(--c-nd-600)">
                      <AddFriends />
                    </LabelS>
                    <LabelS bold color="var(--c-nd-600)">
                      {slot.player.lastName}
                    </LabelS>
                  </Vertical>
                )}
              </EmptySlotWrapper>
            );
          }
        )}
      </Scrollable>
    </Root>
  );
};

SlotsPreview.fragments = {
  cardCollection: gql`
    fragment SlotsPreview_cardCollection on CardCollection {
      slug
      snapshotted
      slotsPreview(forUserSlug: $user, first: 5) {
        slug
        cardCollectionCardsCount
        slot {
          id
          cardPictureUrl
          player {
            slug
            lastName
          }
        }
        highlightedCardCollectionCard {
          id
          anyCard {
            slug
            ...Card_anyCard
          }
        }
      }
    }
    ${Card.fragments.anyCard}
  ` as TypedDocumentNode<SlotsPreview_cardCollection>,
};
